<template>
  <div>
    <modal @close="$emit('close')" size="lg" content-class="em-payment-modal">
    <template #header>
      <div class="d-flex justify-content-between w-100 align-items-start">
        <div class="d-flex align-items-center">
          <strong class="text-primary mr-3" style="font-size: 1.25rem; white-space: nowrap">#{{ member.memberNumber }}</strong>
          <div>
            <div>{{ member.primaryName }}</div>
            <div class="text-muted small">{{ member.email }}</div>
            <div class="text-muted small">{{ member.phone }}</div>
          </div>
        </div>
        <div v-if="invoices.length" class="text-left">
          <div v-for="inv in invoices" :key="inv.id" class="mb-1">
            <div>Invoice #{{ inv.invoice_number }} <span class="text-muted small">({{ inv.is_sent ? 'Sent' : 'Not Sent' }})</span></div>
            <div>Total: <strong>{{ inv.total_cost.toFixed(2) }}</strong></div>
          </div>
        </div>
      </div>
    </template>

    <template #body>
      <div v-if="loadingStatus" class="text-center py-3">
        <div class="spinner-border spinner-border-sm" />
      </div>
      <template v-else>
        <div v-if="errorMessage" class="alert alert-warning">{{ errorMessage }}</div>
        <grid-table
          :columns="tableColumns"
          :rows="localMembers"
          :loading="false"
          :no-stripes="true"
          border-mode="rows"
        >
          <template #cell:name="{ row }">
            <strong>{{ row.name }}</strong>
            <span v-if="row.upgraded_at != null" class="badge alert-success ml-1">Upgraded</span>
            <span v-else-if="row.status === 'scratched'" class="badge alert-danger ml-1">Scratched</span>
          </template>

          <template #cell:cost="{ row }">${{ row.cost.toFixed(2) }}</template>

          <template #cell:paid="{ row }">
            <app-checkbox :id="'em-paid-' + row.id" :value="row.is_paid" :disabled="row.status === 'scratched'" @input="row.is_paid = $event" />
          </template>

          <template #cell:action="{ row }">
            <template v-if="row.status === 'scratched'">
              <app-button
                v-if="row.can_unscratch"
                size="sm"
                :block="false"
                :loading="unscratchingId === row.id"
                @click="promptUnscratch(row)"
              >Unscratch</app-button>
              <app-button
                v-else
                size="sm"
                :block="false"
                disabled
              >Scratched</app-button>
            </template>
            <app-button
              v-else
              size="sm"
              :block="false"
              :loading="scratchingId === row.id"
              @click="onScratch(row)"
            >Scratch</app-button>
          </template>
        </grid-table>

        <div class="d-flex align-items-center justify-content-end mt-2">
          <strong class="mr-2 mb-3" style="font-size: 1.1rem">Payment type</strong>
          <div style="width: 160px">
            <f-search-select
              id="em_payment_comment"
              :value="commit"
              :options="commitOptions"
              :searchable="false"
              @input="commit = $event"
            />
          </div>
        </div>
      </template>
    </template>

    <template #footer>
      <div class="d-flex justify-content-start w-100">
        <app-button variant="outline-primary" :block="false" :loading="saving" class="mr-2" @click="onUpdate">Update</app-button>
        <app-button variant="outline-primary" :block="false" @click="$emit('close')">Cancel</app-button>
      </div>
    </template>
    </modal>

    <confirm-modal
      v-if="pendingUnscratch"
      :loading="unscratchingId != null"
      :message="`Unscratch ${pendingUnscratch.name}? They will be restored to the draft. The pending refund will be cancelled, or — if it has already been paid out — a new Event Membership Fee invoice will be issued and sent.`"
      @confirm="confirmUnscratch"
      @cancel="pendingUnscratch = null"
    />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from '@vue/composition-api'
import Modal from '@/components/Modal'
import AppButton from '@/components/AppButton'
import AppCheckbox from '@/components/AppCheckbox'
import GridTable from '@/components/GridTable'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import ConfirmModal from '@/components/ConfirmModal'
import draftEventMembersApi from '@/services/api/draftEventMembers'
import EventBus from '@/store/EventBus'

const TABLE_COLUMNS = [
  { key: 'name', label: '', width: '2fr' },
  { key: 'cost', label: 'Cost', width: '1fr' },
  { key: 'paid', label: 'Paid', width: '0.5fr' },
  { key: 'action', label: 'Action', width: '1fr' }
]

export default defineComponent({
  name: 'EventMemberPaymentModal',
  components: { Modal, AppButton, AppCheckbox, GridTable, FSearchSelect, ConfirmModal },
  props: {
    member: { type: Object, required: true },
    draftId: { type: [String, Number], required: true }
  },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const loadingStatus = ref(true)
    const saving = ref(false)
    const scratchingId = ref(null)
    const unscratchingId = ref(null)
    const pendingUnscratch = ref(null)
    const localMembers = ref([])
    const invoices = ref([])
    const commitOptions = ref([])
    const commit = ref(null)
    const errorMessage = ref(null)

    async function fetchStatus() {
      loadingStatus.value = true
      try {
        const res = await draftEventMembersApi.getPaymentStatus(props.draftId, props.member.id)
        const data = res.data.data
        localMembers.value = data.members.map(m => ({ ...m }))
        invoices.value = data.invoices || (data.invoice ? [data.invoice] : [])

        const methods = data.payment_methods
        commitOptions.value = methods.list
          .filter(v => !methods.disabled.includes(v))
          .map(v => ({ id: v, name: v }))
        commit.value = methods.selected
      } finally {
        loadingStatus.value = false
      }
    }

    async function onUpdate() {
      saving.value = true
      errorMessage.value = null
      try {
        const members = {}
        localMembers.value.forEach((m) => {
          members[m.id] = { is_paid: m.is_paid }
        })
        const res = await draftEventMembersApi.updatePaymentStatus(
          props.draftId,
          props.member.id,
          { members, commit: commit.value }
        )
        if (res.data.message) EventBus.$emit('show-success', res.data.message)
        emit('updated')
        emit('close')
      } catch (err) {
        errorMessage.value = err.response?.data?.message || 'Something went wrong. Please try again.'
      } finally {
        saving.value = false
      }
    }

    async function onScratch(sub) {
      scratchingId.value = sub.id
      try {
        await draftEventMembersApi.updateMemberStatus(props.draftId, sub.id)
        sub.status = 'scratched'
        sub.is_paid = false
        sub.can_unscratch = true
        emit('updated')
      } catch (_) {
        // http.js handles toast
      } finally {
        scratchingId.value = null
      }
    }

    function promptUnscratch(sub) {
      errorMessage.value = null
      pendingUnscratch.value = sub
    }

    async function confirmUnscratch() {
      const sub = pendingUnscratch.value
      if (!sub) return

      unscratchingId.value = sub.id
      try {
        const res = await draftEventMembersApi.unscratchMemberStatus(props.draftId, sub.id)
        if (res.data.message) EventBus.$emit('show-success', res.data.message)
        pendingUnscratch.value = null
        await fetchStatus()
        emit('updated')
      } catch (err) {
        errorMessage.value = err.response?.data?.message || 'Something went wrong. Please try again.'
        pendingUnscratch.value = null
      } finally {
        unscratchingId.value = null
      }
    }

    onMounted(fetchStatus)

    return {
      tableColumns: TABLE_COLUMNS,
      loadingStatus,
      saving,
      scratchingId,
      unscratchingId,
      pendingUnscratch,
      localMembers,
      invoices,
      commitOptions,
      commit,
      errorMessage,
      onUpdate,
      onScratch,
      promptUnscratch,
      confirmUnscratch
    }
  }
})
</script>

<style>
.em-payment-modal .modal-header .close {
  display: none;
}
</style>



// WEBPACK FOOTER //
// src/components/DraftEventMembers/EventMemberPaymentModal.vue